import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import { encodeOpenMetadataFqn } from '@/shared/lib/format';

import type { TagItem } from '@/entities/tags';

export type TableConstraint = {
  constraintType: 'PRIMARY_KEY' | 'FOREIGN_KEY' | 'UNIQUE' | 'NOT_NULL';
  columns: string[];
  referredColumns?: string[];
  relationshipType?: string;
};

export type { JsonPatchOperation } from '@/shared/types/common';

export type Domain = {
  id: string;
  type: 'domain';
  name: string;
  fullyQualifiedName: string;
  displayName?: string;
  description?: string;
  href?: string;
};

export type DataProduct = {
  id: string;
  type: 'dataProduct';
  name: string;
  fullyQualifiedName: string;
  displayName?: string;
  description?: string;
  href?: string;
};

export type Certification = {
  tagLabel: {
    tagFQN: string;
    name: string;
    displayName?: string;
    description?: string;
    style?: {
      color?: string;
      iconURL?: string;
    };
    source?: string;
    labelType?: string;
    state?: string;
  };
  appliedDate: number;
  expiryDate: number;
};

export type LifeCycle = {
  created?: {
    timestamp: number;
  };
  updated?: {
    timestamp: number;
  };
};

export type AssetBaseInfo = {
  id: string;
  name: string;
  displayName?: string;
  fullyQualifiedName: string;
  description?: string;
  tags?: TagItem[];
  owner?: {
    id: string;
    name: string;
    displayName?: string;
    type: string;
  };
  owners?: Array<{
    id: string;
    name: string;
    displayName?: string;
    type: string;
  }>;
  service?: {
    id: string;
    type: string;
    name: string;
    displayName: string;
  };
  database?: {
    id: string;
    type: string;
    name: string;
    displayName: string;
    fullyQualifiedName?: string;
  };
  databaseSchema?: {
    id: string;
    type: string;
    name: string;
    displayName: string;
  };
  tier?: {
    tagFQN: string;
    name: string;
  };
  domains?: Domain[];
  dataProducts?: DataProduct[];
  version?: number;
  updatedAt?: number;
  updatedBy?: string;
  lifeCycle?: LifeCycle;
  deleted?: boolean;
  entityStatus?: string;
  certification?: Certification;
  tableConstraints?: TableConstraint[];
  columns?: Array<{
    name: string;
    dataType: string;
    dataTypeDisplay?: string;
    constraint?: string;
  }>;
  numberOfObjects?: number;
  partitions?: number;
  replicationFactor?: number;
  cleanupPolicies?: string[];
  maximumMessageSize?: number;
  messageSchema?: {
    schemaType?: string;
  };
  tableType?: string;
  queries?: unknown[];
  usageSummary?: {
    weeklyStats?: {
      percentileRank?: number;
    };
  };
  databaseSchemas?: Array<{
    name: string;
    displayName?: string;
    description?: string;
  }>;
  rowCount?: number;
  profile?: {
    rowCount?: number;
  };
  algorithm?: string;
  mlStore?: {
    storage?: string;
  };
  sourceUrl?: string;
  serviceType?: string;
};

export const fetchTableBaseInfo = async (fqn: string): Promise<AssetBaseInfo> => {
  const encodedFqn = encodeOpenMetadataFqn(fqn);
  const response = await openMetadataApiClient.get<AssetBaseInfo>(`/tables/name/${encodedFqn}`, {
    params: {
      fields:
        'followers,joins,tags,owners,dataModel,tableConstraints,schemaDefinition,domains,votes,extension,usageSummary,testSuite',
      include: 'all',
    },
  });

  return response.data;
};

export const fetchContainerBaseInfo = async (fqn: string): Promise<AssetBaseInfo> => {
  const encodedFqn = encodeOpenMetadataFqn(fqn);
  const response = await openMetadataApiClient.get<AssetBaseInfo>(`/containers/name/${encodedFqn}`, {
    params: {
      fields: 'parent,dataModel,owners,tags,followers,extension,domains,votes',
      include: 'all',
    },
  });

  return response.data;
};

export const fetchTopicBaseInfo = async (fqn: string): Promise<AssetBaseInfo> => {
  const encodedFqn = encodeOpenMetadataFqn(fqn);
  const response = await openMetadataApiClient.get<AssetBaseInfo>(`/topics/name/${encodedFqn}`, {
    params: {
      fields: 'owners,followers,tags,domains,votes,extension',
      include: 'all',
    },
  });

  return response.data;
};

export const fetchDatabaseBaseInfo = async (fqn: string): Promise<AssetBaseInfo> => {
  const encodedFqn = encodeOpenMetadataFqn(fqn);
  const response = await openMetadataApiClient.get<AssetBaseInfo>(`/databases/name/${encodedFqn}`, {
    params: {
      fields: 'owners,tags,domains,votes,extension,followers',
      include: 'all',
    },
  });

  return response.data;
};

export const fetchDatabaseSchemaBaseInfo = async (fqn: string): Promise<AssetBaseInfo> => {
  const encodedFqn = encodeOpenMetadataFqn(fqn);
  const response = await openMetadataApiClient.get<AssetBaseInfo>(`/databaseSchemas/name/${encodedFqn}`, {
    params: {
      fields: 'owners,usageSummary,tags,domains,votes,extension,followers',
      include: 'all',
    },
  });

  return response.data;
};

export const fetchGlossaryTermBaseInfo = async (fqn: string): Promise<AssetBaseInfo> => {
  const encodedFqn = encodeOpenMetadataFqn(fqn);
  const response = await openMetadataApiClient.get<AssetBaseInfo>(`/glossaryTerms/name/${encodedFqn}`, {
    params: {
      fields: 'relatedTerms,reviewers,tags,owners,children,votes,domains,extension',
      include: 'all',
    },
  });

  return response.data;
};

export const fetchTagBaseInfo = async (fqn: string): Promise<AssetBaseInfo> => {
  const encodedFqn = encodeOpenMetadataFqn(fqn);
  const response = await openMetadataApiClient.get<AssetBaseInfo>(`/tags/name/${encodedFqn}`, {
    params: {
      fields: 'domains,owners',
      include: 'all',
    },
  });

  return response.data;
};

export const fetchServiceBaseInfo = async (fqn: string): Promise<AssetBaseInfo> => {
  const parts = fqn.split('/');
  if (parts.length !== 2) {
    throw new Error(
      `잘못된 서비스 FQN 형식입니다: ${fqn}\n올바른 형식: serviceType/serviceName (예: databaseServices/postgres)`
    );
  }

  const [serviceType, serviceName] = parts;
  const normalizedServiceType = serviceType.endsWith('s') ? serviceType : `${serviceType}s`;

  try {
    const response = await openMetadataApiClient.get<AssetBaseInfo>(
      `/services/${normalizedServiceType}/name/${serviceName}`,
      {
        params: {
          fields: 'owners,tags,followers,domains',
          include: 'all',
        },
      }
    );

    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`서비스 정보를 불러올 수 없습니다: ${serviceName} (${serviceType})\n${error.message}`);
    }
    throw error;
  }
};
